import React, { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, Layers } from "lucide-react";

export default function AddonsMatrix({ site }) {
  const [groups, setGroups] = useState([]);
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingGroup, setSavingGroup] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sites/${site.id}/addons`);
      setGroups(r.data || []);
      const o = {};
      (r.data || []).forEach((g) => (o[g.id] = JSON.stringify(g.fields)));
      setOriginal(o);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [site.id]);

  useEffect(() => {
    load();
  }, [load]);

  const setPrice = (gid, fid, price) =>
    setGroups((gs) =>
      gs.map((g) =>
        g.id === gid ? { ...g, fields: g.fields.map((f) => (f.id === fid ? { ...f, price } : f)) } : g
      )
    );

  const isDirty = (g) => original[g.id] !== JSON.stringify(g.fields);

  const saveGroup = async (g) => {
    setSavingGroup(g.id);
    try {
      await api.put(`/sites/${site.id}/addons`, { group_id: g.id, fields: g.fields });
      setOriginal((o) => ({ ...o, [g.id]: JSON.stringify(g.fields) }));
      toast.success(`${g.name} saved`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingGroup(null);
    }
  };

  if (loading)
    return (
      <p className="text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin inline" /> Loading add-ons…
      </p>
    );

  if (groups.length === 0)
    return (
      <div className="rounded-lg border border-dashed border-border py-16 text-center">
        <Layers className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">
          No product add-ons found. Live sites need the WooCommerce Product Add-Ons plugin (v6.9+).
        </p>
      </div>
    );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-heading text-xl font-semibold">Add-Ons & Half-and-Half Pricing</h3>
        <p className="text-sm text-muted-foreground">
          Edit extra topping, crust and half-and-half surcharge prices across the store.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {groups.map((g) => (
          <div
            key={g.id}
            className="rounded-lg border border-border bg-card p-4 space-y-3"
            data-testid={`addon-group-${g.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-amber-400" />
                <h4 className="font-heading font-semibold">{g.name}</h4>
              </div>
              <Badge variant="secondary" className="text-[10px] uppercase">
                {g.type}
              </Badge>
            </div>
            <div className="space-y-2">
              {g.fields.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-2">
                  <span className="text-sm">{f.label}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground text-sm">$</span>
                    <Input
                      value={f.price}
                      onChange={(e) => setPrice(g.id, f.id, e.target.value)}
                      className="h-8 w-20 font-mono-num text-sm text-right"
                      data-testid={`addon-price-${f.id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <Button
              size="sm"
              className="w-full"
              disabled={!isDirty(g) || savingGroup === g.id}
              variant={isDirty(g) ? "default" : "secondary"}
              onClick={() => saveGroup(g)}
              data-testid={`save-addon-group-${g.id}`}
            >
              {savingGroup === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save {g.name}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
